import React, { useEffect, useRef, useCallback } from 'react';
import {
    RobotOutlined,
    SendOutlined,
    StopOutlined,
    DeleteOutlined,
    CloseOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { useAiChat } from '../../contexts/AiChatContext';
import PropTypes from 'prop-types';
import styles from './AiChatDrawer.module.scss';

// ── Indicateur de frappe animé ─────────────────────────────────
function TypingIndicator() {
    return (
        <div className={`${styles.bubble} ${styles.assistant}`}>
            <div className={`${styles.bubbleContent} ${styles.typingBubble}`}>
                <span className={styles.typingDot} />
                <span className={styles.typingDot} />
                <span className={styles.typingDot} />
            </div>
        </div>
    );
}

ChatLink.propTypes = {
    href: PropTypes.string,
    children: PropTypes.node,
};

// ── Float button ───────────────────────────────────────────────
export function AiChatButton() {
    const { open, toggleOpen, messages } = useAiChat();
    return (
        <Tooltip title={open ? "Fermer l'assistant" : 'Assistant IA'} placement="left">
            <button
                className={`${styles.floatBtn} ${!messages.length && !open ? styles.pulse : ''}`}
                onClick={toggleOpen}
                aria-label="Assistant IA"
            >
                {open ? <CloseOutlined /> : <RobotOutlined />}
            </button>
        </Tooltip>
    );
}

// ── Lien dossier/tâche — navigation SPA SANS fermer le chat (panel reste ouvert) ──
function ChatLink({ href, children }) {
    const navigate = useNavigate();
    const isInternal = href?.startsWith('/');

    if (!isInternal) {
        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    }

    return (
        <a
            href={href}
            onClick={(e) => {
                e.preventDefault();
                navigate(href);
            }}
        >
            {children}
        </a>
    );
}

// ── Message bubble ─────────────────────────────────────────────
function MessageBubble({ message }) {
    const isUser = message.role === 'user';
    return (
        <div className={`${styles.bubble} ${isUser ? styles.user : styles.assistant}`}>
            <div className={styles.bubbleContent}>
                {isUser
                    ? message.content
                    : (
                        <ReactMarkdown components={{ a: ChatLink }}>
                            {message.content || ' '}
                        </ReactMarkdown>
                    )
                }
            </div>
        </div>
    );
}

MessageBubble.propTypes = {
    message: PropTypes.shape({
        role: PropTypes.string,
        content: PropTypes.string,
    }).isRequired,
};

// ── Main panel ─────────────────────────────────────────────────
export function AiChatPanel() {
    const {
        open, closeChat,
        model, setModel, allowedModels,
        providerLabel,
        messages, loading, error,
        sendMessage, cancelRequest, clearMessages,
    } = useAiChat();

    const [input, setInput]  = React.useState('');
    const messagesEndRef     = useRef(null);
    const textareaRef        = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const handleSend = useCallback(() => {
        const text = input.trim();
        if (!text || loading) return;
        setInput('');
        sendMessage(text);
        textareaRef.current?.focus();
    }, [input, loading, sendMessage]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    if (!open) return null;

    const currentModel = allowedModels.find(m => m.value === model);
    const showModelSelector = allowedModels.length > 1;

    return (
        <div className={styles.panel} role="dialog" aria-label="Assistant IA">
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerIcon}><RobotOutlined /></div>
                <div className={styles.headerTitle}>
                    Assistant IA
                    <span>Propulsé par {providerLabel} · {currentModel?.label ?? model}</span>
                </div>
                <div className={styles.headerActions}>
                    {messages.length > 0 && !loading && (
                        <Tooltip title="Effacer la conversation">
                            <button className={styles.headerBtn} onClick={clearMessages} aria-label="Effacer">
                                <DeleteOutlined />
                            </button>
                        </Tooltip>
                    )}
                    <Tooltip title="Fermer">
                        <button className={styles.headerBtn} onClick={closeChat} aria-label="Fermer">
                            <CloseOutlined />
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Model selector */}
            {showModelSelector && (
                <div className={styles.modelBar}>
                    <span className={styles.modelLabel}>Modèle :</span>
                    <select
                        className={styles.modelSelect}
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        disabled={loading}
                    >
                        {allowedModels.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Messages */}
            <div className={styles.messages}>
                {messages.length === 0 && !loading ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}><RobotOutlined /></div>
                        <p className={styles.emptyTitle}>Comment puis-je vous aider ?</p>
                        <p className={styles.emptyHint}>
                            Posez une question sur vos affaires, tâches ou dossiers administratifs.
                        </p>
                    </div>
                ) : (
                    <>
                        {messages.map((msg, i) => (
                            <MessageBubble key={i} message={msg} />
                        ))}
                        {loading && <TypingIndicator />}
                    </>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Error */}
            {error && (
                <div className={styles.errorBar}>
                    <WarningOutlined /> {error}
                </div>
            )}

            {/* Input */}
            <div className={styles.inputZone}>
                <textarea
                    ref={textareaRef}
                    className={styles.textarea}
                    rows={1}
                    placeholder="Écrivez votre message… (Entrée pour envoyer)"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                />
                {loading ? (
                    <Tooltip title="Annuler">
                        <button className={styles.stopBtn} onClick={cancelRequest} aria-label="Annuler">
                            <StopOutlined />
                        </button>
                    </Tooltip>
                ) : (
                    <Tooltip title="Envoyer (Entrée)">
                        <button
                            className={styles.sendBtn}
                            onClick={handleSend}
                            disabled={!input.trim()}
                            aria-label="Envoyer"
                        >
                            <SendOutlined />
                        </button>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}
